import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery, pagedResponseWith } from './pagination.js';
import { IsoDateSchema, MessageSchema } from './primitives.js';
const c = initContract();
/**
 * Leave (HR-04).
 *
 * Balances are CALCULATED from the request ledger — opening + accrued −
 * used − pending — never stored (BR-HR-04-01/-02). A pending request holds
 * its days by existing; cancel/reject releases the hold with nothing written
 * back (BR-HR-04-03).
 *
 * BR-HR-04-19 (owner ruling R1): every ACTIVE type is requestable by
 * everyone; the visibility flag is DELETED from the model. An employee below
 * a type's minimum service is SHOWN the type and told why (BR-HR-04-05) —
 * the balance response carries `eligible` + `ineligibleReason`.
 *
 * Decisions take BOTH gates (16.5.2b, BR-PERM-44): hr.approve (capability)
 * AND being the routed manager (routing). Nobody decides their own
 * (BR-HR-04-12). Rejecting requires a comment shared with the employee
 * (BR-HR-04-10).
 *
 * Days are WORKING days — weekly off-days (Fri/Sat) and public holidays
 * excluded (BR-HR-04-14, the day-counting defect fix).
 */
export const LEAVE_STATUSES = ['pending', 'approved', 'rejected', 'cancelled'];
/** HR-04 §5a — the four types a new tenant is seeded with. */
export const SEED_LEAVE_TYPES = [
    {
        name: 'Annual Leave',
        nameAr: 'إجازة سنوية',
        requiresApproval: true,
        requiresDocument: false,
        documentThresholdDays: null,
        accrualRate: '1.75',
        accrualPeriod: 'monthly',
        openingBalance: '5',
        carryForward: true,
        carryCap: '10',
        minTenureMonths: 3,
    },
    {
        name: 'Sick Leave',
        nameAr: 'إجازة مرضية',
        requiresApproval: true,
        requiresDocument: true,
        documentThresholdDays: 3,
        accrualRate: '12',
        accrualPeriod: 'annual',
        openingBalance: '0',
        carryForward: false,
        carryCap: null,
        minTenureMonths: 0,
    },
    {
        name: 'Unpaid Leave',
        nameAr: 'إجازة بدون راتب',
        requiresApproval: true,
        requiresDocument: false,
        documentThresholdDays: null,
        accrualRate: '0',
        accrualPeriod: 'annual',
        openingBalance: '0',
        carryForward: false,
        carryCap: null,
        minTenureMonths: 0,
    },
    {
        name: 'Parental Leave',
        nameAr: 'إجازة أبوة/أمومة',
        requiresApproval: true,
        requiresDocument: true,
        documentThresholdDays: null,
        accrualRate: '90',
        accrualPeriod: 'annual',
        openingBalance: '0',
        carryForward: false,
        carryCap: null,
        minTenureMonths: 12,
    },
];
export const LEAVE_MESSAGES = {
    /** BR-HR-04-08's server half — ERR-HR-04-01, both dates. */
    incomplete: 'Set both dates with a span above zero.',
    /**
     * ERR-HR-04-02 (the red required marker) and ERR-HR-04-03, which is the same
     * situation with the greyed button CLICKED ANYWAY — in the prototype the
     * request was created without the document. This sentence is the floor that
     * stops that, and it is why the rule is enforced by the submit ACTION rather
     * than by the button's disabled attribute.
     */
    documentRequired: 'This leave type requires a supporting document.',
    /**
     * BR-HR-04-05 / ERR-HR-04-06: shown WITH the type, never instead of it. The
     * prototype showed a new joiner a parental-leave balance of zero and no
     * explanation whatsoever; naming the service requirement IS the explanation.
     */
    notEligible: (months) => `Available after ${months} months of service.`,
    /** BR-HR-04-17 / ERR-HR-04-09 — the spec's sentence, verbatim including its two routes out. */
    approveDenied: 'You do not have permission to approve leave. Ask an administrator to grant it, or reassign the approval.',
    /** BR-HR-04-18 / 16.5.2b — permitted but not routed. */
    notRouted: (manager) => `This request is routed to ${manager}. Only they (or the owner) can decide it.`,
    /** BR-HR-04-12 / ERR-HR-04-08 — the prototype prevented nothing. */
    ownRequest: 'You cannot decide your own leave request.',
    /** BR-HR-04-10 / ERR-HR-04-07 — the prototype's Reject did nothing and said nothing. */
    rejectCommentRequired: 'A comment is required and shared with the employee.',
    approvedFlows: 'Approved — dates now flow to Attendance as On Leave.',
    rejectedReleased: 'Rejected — pending hold released.',
    cancelledReleased: 'Cancelled — pending hold released.',
    alreadyDecided: 'This request has already been decided.',
    onlyRequesterCancels: 'Only the requester can cancel a pending request.',
    /** The §4 banner — printed permanently on the screen. */
    banner: 'Approved leave is read directly by the Attendance module — no duplicate entry.',
    /** Over-balance warns, never blocks (§14 ERR-HR-04-04). */
    overBalance: (available) => `This request exceeds the available balance of ${available} days.`,
    noEmployeeRecord: 'Your account is not linked to an employee record. Ask HR.',
};
/**
 * D46 — HR-04's leave-routing refusal, structured. `manager` is a display name.
 * The sentence is byte-identical to attendance's `notRouted`, but each member
 * keeps its own key (`hr.errLeaveNotRouted`) so the census's one-key-per-row
 * rule holds. Sentence parity pinned by `refusal-census.spec.ts`.
 */
export const LEAVE_REFUSALS = {
    notRouted: (manager) => ({
        message: LEAVE_MESSAGES.notRouted(manager),
        key: 'hr.errLeaveNotRouted',
        params: { manager },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const LeaveTypeSchema = z
    .object({
    id: z.uuid(),
    name: z.string(),
    nameAr: z.string(),
    requiresApproval: z.boolean(),
    requiresDocument: z.boolean(),
    documentThresholdDays: z.number().int().nullable(),
    accrualRate: z.string(),
    accrualPeriod: z.enum(['monthly', 'annual']),
    openingBalance: z.string(),
    carryForward: z.boolean(),
    carryCap: z.string().nullable(),
    minTenureMonths: z.number().int(),
    active: z.boolean(),
})
    .strict();
/** HR-04 §5b — the derived balance card, with the eligibility sentence. */
export const LeaveBalanceSchema = z
    .object({
    employeeId: z.uuid(),
    leaveTypeId: z.uuid(),
    eligible: z.boolean(),
    /** BR-HR-04-05: why the type is not yet available, or null. */
    ineligibleReason: z.string().nullable(),
    opening: z.string(),
    accrued: z.string(),
    used: z.string(),
    pending: z.string(),
    available: z.string(),
})
    .strict();
export const LeaveRequestSchema = z
    .object({
    id: z.uuid(),
    employeeId: z.uuid(),
    employeeName: z.string(),
    leaveTypeId: z.uuid(),
    leaveTypeName: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    halfDay: z.boolean(),
    /** WORKING days (BR-HR-04-14), snapshotted at submit. */
    days: z.string(),
    reason: z.string().nullable(),
    documentName: z.string().nullable(),
    status: z.enum(LEAVE_STATUSES),
    approverEmployeeId: z.uuid().nullable(),
    approverName: z.string().nullable(),
    decidedBy: z.uuid().nullable(),
    decidedAt: z.string().nullable(),
    decisionComment: z.string().nullable(),
    /** BR-HR-04-16: overlapping approved/pending leave in the same team. */
    overlaps: z.string().array(),
    createdAt: z.string(),
})
    .strict();
export const SubmitLeaveRequestSchema = z
    .object({
    /** Omitted = the caller's own employee record (rung-4 floor). */
    employeeId: z.uuid().optional(),
    leaveTypeId: z.uuid(),
    startDate: IsoDateSchema,
    endDate: IsoDateSchema,
    halfDay: z.boolean().optional(),
    reason: z.string().trim().max(500).optional(),
    documentName: z.string().trim().max(200).optional(),
})
    .strict();
export const LeaveTypeUpdateSchema = z
    .object({
    name: z.string().trim().min(1).max(80).optional(),
    nameAr: z.string().trim().min(1).max(80).optional(),
    requiresApproval: z.boolean().optional(),
    requiresDocument: z.boolean().optional(),
    documentThresholdDays: z.number().int().min(0).nullable().optional(),
    accrualRate: z.string().regex(/^\d{1,4}(\.\d{1,2})?$/).optional(),
    accrualPeriod: z.enum(['monthly', 'annual']).optional(),
    openingBalance: z.string().regex(/^\d{1,4}(\.\d{1,2})?$/).optional(),
    carryForward: z.boolean().optional(),
    carryCap: z.string().regex(/^\d{1,4}(\.\d{1,2})?$/).nullable().optional(),
    minTenureMonths: z.number().int().min(0).optional(),
    active: z.boolean().optional(),
})
    .strict();
const requestParams = z.object({ requestId: z.uuid() }).strict();
export const leaveContract = c.router({
    listTypes: {
        method: 'GET',
        path: '/api/hr/leave/types',
        responses: {
            200: z.object({ types: LeaveTypeSchema.array() }).strict(),
            401: MessageSchema,
        },
        summary: 'Every ACTIVE leave type, for EVERY signed-in user (BR-HR-04-19 — an entitlement is not a display preference). Rung-4 floor: requesting your own leave needs no role.',
    },
    updateType: {
        method: 'PATCH',
        path: '/api/hr/leave/types/:leaveTypeId',
        pathParams: z.object({ leaveTypeId: z.uuid() }).strict(),
        body: LeaveTypeUpdateSchema,
        responses: {
            200: LeaveTypeSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Edit the policy (the leave-policy configuration permission). Balances recalculate live from these rules (BR-HR-04-15) — they are derived, so there is nothing to migrate.',
    },
    myBalances: {
        method: 'GET',
        path: '/api/hr/leave/my-balances',
        responses: {
            200: z.object({ balances: LeaveBalanceSchema.array() }).strict(),
            401: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Your own balance per active type — rung-4 floor, derived fresh (BR-HR-04-01).',
    },
    /**
     * The Balances tab, ONE PAGE at a time (NFR-42).
     *
     * Measured at the third paging wave's 5,000 employees, this route answered
     * with **13.7 MB** — the largest single response left in the product after
     * the item catalogue was paged, and larger than the 10.5 MB MFG-03 screen
     * the second wave took. The reason is the shape rather than the row count:
     * every employee carries a balance for EVERY active type, so the payload is
     * employees × types and the tab's own rule (BR-HR-04-19, "a column per
     * type") is what multiplies it.
     *
     * `types` stays WHOLE beside the page, and that is deliberate: it is the
     * COLUMN HEADINGS. A page that narrowed the columns as well as the rows
     * would be a table that changes shape as it is read.
     *
     * Nothing here is a derived COLUMN in §3 step 11's sense: a row's balance
     * is that employee's own opening + accrual − used − pending, and depends on
     * no row above it. The page is a page of people, and each person's figures
     * are the same figures whichever page they land on.
     */
    balances: {
        method: 'GET',
        path: '/api/hr/leave/balances',
        query: pagedQuery({ q: z.string().trim().min(1).max(120).optional() }),
        responses: {
            200: pagedResponseWith('rows', z
                .object({
                employeeId: z.uuid(),
                code: z.string(),
                fullName: z.string(),
                fullNameAr: z.string(),
                balances: LeaveBalanceSchema.array(),
            })
                .strict(), { types: LeaveTypeSchema.array() }),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The Balances tab (hr.view), PAGED (NFR-42): a page of employees × EVERY active type — a column per type, Parental included (BR-HR-04-19). Measured at 13.7 MB unpaged.',
    },
    listRequests: {
        method: 'GET',
        path: '/api/hr/leave/requests',
        query: pagedQuery({
            scope: z.enum(['mine', 'approvals', 'all']).optional(),
            status: z.enum(LEAVE_STATUSES).optional(),
        }),
        responses: {
            200: pagedResponseWith('requests', LeaveRequestSchema, {
                scope: z.enum(['mine', 'approvals', 'all']),
            }),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'mine = your own (rung 4). approvals = pending requests ROUTED to you (hr.approve; the owner sees all — BR-HR-04-13/-18). all = hr.view. PAGED (NFR-42): the scope is a PREDICATE on the resolved scope, and BR-HR-04-16\u2019s overlap warning is its own windowed read rather than a fold over the page.',
    },
    submit: {
        method: 'POST',
        path: '/api/hr/leave/requests',
        body: SubmitLeaveRequestSchema,
        responses: {
            201: z.object({ request: LeaveRequestSchema, warnings: z.string().array() }).strict(),
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Submit (own = rung 4; another’s = hr.create): dates + span > 0 + any required document ENFORCED BY THE ACTION (BR-HR-04-08); days counted as working days (BR-HR-04-14); routed to the employee’s manager (BR-HR-04-09); the pending hold starts immediately. Over-balance warns, never blocks.',
    },
    cancel: {
        method: 'POST',
        path: '/api/hr/leave/requests/:requestId/cancel',
        pathParams: requestParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: LeaveRequestSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Withdraw a pending request — the requester only. The hold releases itself (BR-HR-04-03).',
    },
    approve: {
        method: 'POST',
        path: '/api/hr/leave/requests/:requestId/approve',
        pathParams: requestParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: z.object({ request: LeaveRequestSchema, message: z.string() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'BOTH gates (BR-PERM-44): hr.approve AND the routed manager (owner passes; unrouted falls to any holder). Nobody approves their own (BR-HR-04-12). Attendance READS the approved row — nothing is written into it (BR-HR-04-11).',
    },
    reject: {
        method: 'POST',
        path: '/api/hr/leave/requests/:requestId/reject',
        pathParams: requestParams,
        body: z.object({ comment: z.string().trim().min(1).max(500) }).strict(),
        responses: {
            200: z.object({ request: LeaveRequestSchema, message: z.string() }).strict(),
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Reject with a REQUIRED comment, shared with the employee (BR-HR-04-10). The hold releases.',
    },
    calendar: {
        method: 'GET',
        path: '/api/hr/leave/calendar',
        query: z.object({ month: z.string().regex(/^\d{4}-\d{2}$/) }).strict(),
        responses: {
            200: z
                .object({
                month: z.string(),
                daysInMonth: z.number().int(),
                rows: z
                    .object({
                    employeeId: z.uuid(),
                    code: z.string(),
                    fullName: z.string(),
                    fullNameAr: z.string(),
                    /** Per day: none | approved (solid) | pending (striped) | off. */
                    days: z.enum(['none', 'approved', 'pending', 'off']).array(),
                })
                    .strict()
                    .array(),
            })
                .strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The team calendar (hr.view): approved solid, pending striped, weekends/holidays grey — spot coverage gaps.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=leave.js.map